import { createFileRoute, Link } from "@tanstack/react-router"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { useState } from "react"
import { Plus, Pencil, ExternalLink } from "lucide-react"
import { toast } from "sonner"
import { api, type ApiEnvelope, type Page } from "@/lib/api"
import { getErrorMessage, getFieldErrors } from "@/lib/get-error-message"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Textarea } from "@/components/ui/textarea"
import { FieldError } from "@/components/ui/field-error"
import { Switch } from "@/components/ui/switch"
import { Badge } from "@/components/ui/badge"
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog"
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table"
import { ConfirmDelete } from "@/components/admin/confirm-delete"
import { RichTextEditor } from "@/components/admin/rich-text-editor"

export const Route = createFileRoute("/admin/_authed/pages")({
  component: PagesList,
})

function slugify(value: string) {
  return value
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "")
}

type FormState = {
  title: string
  slug: string
  excerpt: string
  content: string
  is_published: boolean
}
const empty: FormState = { title: "", slug: "", excerpt: "", content: "", is_published: true }

function PagesList() {
  const queryClient = useQueryClient()
  const { data, isLoading, isError } = useQuery<Page[]>({
    queryKey: ["admin", "pages"],
    queryFn: async () => (await api.get<ApiEnvelope<Page[]>>("/admin/pages")).data.data,
  })

  const [open, setOpen] = useState(false)
  const [editing, setEditing] = useState<Page | null>(null)
  const [form, setForm] = useState<FormState>(empty)
  const [slugTouched, setSlugTouched] = useState(false)
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({})

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ["admin", "pages"] })

  const save = useMutation({
    mutationFn: async () => {
      const payload = {
        title: form.title,
        slug: form.slug,
        excerpt: form.excerpt,
        content: form.content,
        is_published: form.is_published,
      }
      if (editing) {
        await api.put(`/admin/pages/${editing.slug}`, payload)
      } else {
        await api.post("/admin/pages", payload)
      }
    },
    onSuccess: () => {
      invalidate()
      setOpen(false)
      setFieldErrors({})
      toast.success(editing ? "Page updated." : "Page created.")
    },
    onError: (error) => {
      setFieldErrors(getFieldErrors(error))
      toast.error(getErrorMessage(error))
    },
  })

  const remove = useMutation({
    mutationFn: async (slug: string) => api.delete(`/admin/pages/${slug}`),
    onSuccess: () => {
      invalidate()
      toast.success("Page deleted.")
    },
    onError: (error) => {
      toast.error(getErrorMessage(error))
    },
  })

  const openCreate = () => {
    setEditing(null)
    setForm(empty)
    setSlugTouched(false)
    setFieldErrors({})
    setOpen(true)
  }
  const openEdit = (page: Page) => {
    setEditing(page)
    setSlugTouched(true)
    setForm({
      title: page.title,
      slug: page.slug,
      excerpt: page.excerpt ?? "",
      content: page.content ?? "",
      is_published: page.is_published,
    })
    setFieldErrors({})
    setOpen(true)
  }

  return (
    <div>
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-display text-3xl font-bold">Pages</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Standalone pages, reachable at their own URL — link one from Menu to add it to the site header.
          </p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button onClick={openCreate} className="rounded-full">
              <Plus className="h-4 w-4" /> New page
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{editing ? "Edit page" : "New page"}</DialogTitle>
            </DialogHeader>
            <form
              className="space-y-4"
              noValidate
              onSubmit={(e) => {
                e.preventDefault()
                save.mutate()
              }}
            >
              <div className="grid gap-4 sm:grid-cols-2">
                <div>
                  <Label htmlFor="title">Title</Label>
                  <Input
                    id="title"
                    value={form.title}
                    onChange={(e) => {
                      const title = e.target.value
                      setForm((f) => ({ ...f, title, slug: slugTouched ? f.slug : slugify(title) }))
                    }}
                    aria-invalid={!!fieldErrors.title}
                  />
                  <FieldError message={fieldErrors.title} />
                </div>
                <div>
                  <Label htmlFor="slug">URL</Label>
                  <div className="flex items-center gap-1">
                    <span className="shrink-0 text-sm text-muted-foreground">/</span>
                    <Input
                      id="slug"
                      value={form.slug}
                      onChange={(e) => {
                        setSlugTouched(true)
                        setForm({ ...form, slug: e.target.value })
                      }}
                      placeholder="auto-generated from title"
                      aria-invalid={!!fieldErrors.slug}
                    />
                  </div>
                  <FieldError message={fieldErrors.slug} />
                </div>
              </div>
              <div>
                <Label htmlFor="excerpt">Excerpt</Label>
                <Textarea
                  id="excerpt"
                  value={form.excerpt}
                  onChange={(e) => setForm({ ...form, excerpt: e.target.value })}
                  aria-invalid={!!fieldErrors.excerpt}
                />
                <p className="mt-1 text-xs text-muted-foreground">
                  Shown as an intro under the title and used as the SEO/social-share description.
                </p>
                <FieldError message={fieldErrors.excerpt} />
              </div>
              <div>
                <Label>Content</Label>
                <div className="mt-2">
                  <RichTextEditor value={form.content} onChange={(content) => setForm((f) => ({ ...f, content }))} />
                </div>
              </div>
              <div className="flex items-center gap-3">
                <Switch
                  checked={form.is_published}
                  onCheckedChange={(v) => setForm({ ...form, is_published: v })}
                  id="is_published"
                />
                <Label htmlFor="is_published">Published</Label>
              </div>
              <DialogFooter>
                <Button type="submit" disabled={save.isPending}>
                  {save.isPending ? "Saving..." : "Save"}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="mt-8 rounded-2xl border border-border bg-card">
        {isLoading ? (
          <div className="h-40 animate-pulse rounded-2xl bg-secondary" />
        ) : isError ? (
          <p className="p-6 text-sm text-destructive">Couldn't load pages.</p>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Title</TableHead>
                <TableHead>URL</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {(data ?? []).map((page) => (
                <TableRow key={page.id}>
                  <TableCell>{page.title}</TableCell>
                  <TableCell className="text-muted-foreground">
                    <Link
                      to="/$slug"
                      params={{ slug: page.slug }}
                      target="_blank"
                      className="inline-flex items-center gap-1 hover:text-rust"
                    >
                      /{page.slug} <ExternalLink className="h-3 w-3" />
                    </Link>
                  </TableCell>
                  <TableCell>
                    <Badge variant={page.is_published ? "default" : "secondary"}>
                      {page.is_published ? "Published" : "Draft"}
                    </Badge>
                  </TableCell>
                  <TableCell className="flex justify-end gap-2">
                    <Button variant="outline" size="sm" onClick={() => openEdit(page)}>
                      <Pencil className="h-4 w-4" />
                    </Button>
                    <ConfirmDelete itemLabel={page.title} onConfirm={() => remove.mutate(page.slug)} />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>
    </div>
  )
}
